import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { costChangeStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import { changeStatusView } from '../lib/changes'
import { CHANGE_KIND_KEY } from '../lib/labels'
import { formatMoney, isNegativeMoney } from '../lib/money'
import type { CostChange } from '../types'

interface Props { rows: CostChange[]; currency: string; locale: string; onOpen: (id: string) => void }

const ChangesTable: React.FC<Props> = ({ rows, currency, locale, onOpen }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{t('CostControl_change_col_number')}</TableHead>
            <TableHead>{t('CostControl_change_field_title')}</TableHead>
            <TableHead>{t('CostControl_change_field_contract')}</TableHead>
            <TableHead className="w-40">{t('CostControl_contract_field_status')}</TableHead>
            <TableHead className="w-40 text-right">{t('CostControl_change_col_total')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((c) => {
            const status = changeStatusView(c)
            return (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
                <TableCell className="font-mono text-xs">{c.number}</TableCell>
                <TableCell>
                  <button type="button" className="text-left font-medium text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onOpen(c.id) }}>{c.title}</button>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {c.kind === 'POTENTIAL' ? <Badge variant="neutral">{t(CHANGE_KIND_KEY[c.kind])}</Badge> : <span>{t(CHANGE_KIND_KEY[c.kind])}</span>}
                    {c.takenOver && <Badge variant="secondary">{t('CostControl_change_taken_over')}</Badge>}
                    {c.drawsProvisional && <Badge variant="secondary">{t('CostControl_provisional_draws_badge')}</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{c.contractName ? (c.contractNumber ? `${c.contractNumber} · ${c.contractName}` : c.contractName) : '—'}</TableCell>
                <TableCell><Badge variant={costChangeStatusVariant(status.tone)}>{t(status.key)}</Badge></TableCell>
                <TableCell className={cn('text-right tabular-nums', isNegativeMoney(c.total) && 'text-destructive')}>{money(c.total)}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
export default ChangesTable
